import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import { formatMoney } from '../daily-brief/format';
import type { DailyBriefModel } from '../daily-brief/types';
import type { ExecutiveKpiStripModel } from '../executive-kpis/types';
import type { MasterOverlayModel } from '../master-overlay/types';
import type { OpsIntelligenceModel } from '../ops-intelligence/types';
import { colSpanClass } from '../sections';
import {
  buildSummary,
  buildSwot,
  timelineRows,
  type RestBlocksModel,
} from './loadRestBlocks';

function activityTitle(key: string): string {
  return key.replace(/_/g, ' ');
}

function formatSyncTime(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString();
}

function Card({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={`widget-${id}`}
      data-widget-id={id}
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface)] p-4"
    >
      <h2
        id={`${id}-heading`}
        className="text-sm font-semibold tracking-tight text-[var(--shell-fg)]"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export function RestBlocksGrid({
  rest,
  brief,
  kpis,
  ops,
  overlay,
  loading,
  error,
  onRetry,
}: {
  rest: RestBlocksModel | null;
  brief: DailyBriefModel | null;
  kpis: ExecutiveKpiStripModel | null;
  ops: OpsIntelligenceModel | null;
  overlay: MasterOverlayModel | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const swot = buildSwot(ops, brief);
  const summary = buildSummary(brief, kpis);
  const timeline = timelineRows(overlay);
  const domains = brief?.coverage.domains ?? [];

  return (
    <div className="grid grid-cols-12 gap-6">
      {error && (
        <div
          role="alert"
          className="col-span-12 rounded-[var(--shell-radius-md)] bg-[var(--shell-danger)]/10 px-4 py-3 text-sm text-[var(--shell-danger)]"
        >
          {error}{' '}
          <button type="button" className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      <div className={colSpanClass(6)}>
        <Card id="ops-inventory" title="Inventory Intelligence">
          {loading && !rest ? (
            <p className={shell.muted}>Loading inventory sample…</p>
          ) : (
            <ul className="space-y-1 text-sm">
              <li>
                Fulfillable (sample):{' '}
                <strong>{rest?.inventory.available ?? '—'}</strong>
              </li>
              <li>
                Inbound (sample): <strong>{rest?.inventory.inbound ?? '—'}</strong>
              </li>
              <li>
                Zero-stock SKUs in sample: <strong>{rest?.inventory.zero ?? '—'}</strong>
                {rest?.inventory.sampled ? ` / ${rest.inventory.sampled}` : ''}
              </li>
              <li>
                FBA fulfillable (report sample):{' '}
                <strong>{rest?.inventory.fbaFulfillable ?? '—'}</strong>
              </li>
              <li>
                FBA inbound working (report sample):{' '}
                <strong>{rest?.inventory.fbaInbound ?? '—'}</strong>
              </li>
              <li className={shell.muted}>
                {rest?.inventory.fbaRows
                  ? `${rest.inventory.fbaRows} FBA report rows${rest.inventory.fbaTruncated ? ', page 1 only' : ''}.`
                  : 'No FBA inventory report rows yet.'}
              </li>
              <li>
                <Link className="underline" to={rest?.inventory.href ?? '/amazon/inventory'}>
                  Open inventory
                </Link>
              </li>
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-finance" title="Financial Overview">
          {rest?.finance.error ? (
            <p className={shell.muted}>{rest.finance.error}</p>
          ) : rest && rest.finance.count === 0 && !rest.finance.settlementRows ? (
            <p className={shell.muted}>No finance events or settlement report rows for this connection.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              <li>
                Refunds (sample):{' '}
                <strong>{formatMoney(rest?.finance.refund ?? 0)}</strong>
              </li>
              <li>
                Fees (sample): <strong>{formatMoney(rest?.finance.fee ?? 0)}</strong>
              </li>
              <li>
                Other posted (sample):{' '}
                <strong>{formatMoney(rest?.finance.other ?? 0)}</strong>
              </li>
              {(rest?.finance.events ?? []).map((e) => (
                <li key={e.event_id} className="flex justify-between gap-3">
                  <span className="truncate">
                    {e.event_type || e.transaction_type || e.fee_type || e.event_id}
                  </span>
                  <span className="tabular-nums">
                    {e.amount != null ? formatMoney(Number(e.amount), e.currency) : '—'}
                  </span>
                </li>
              ))}
              <li className={shell.muted}>
                Showing {rest?.finance.count ?? 0}
                {rest?.finance.total != null ? ` of ${rest.finance.total}` : ''} finance events.
                {rest?.finance.settlementRows
                  ? ` Settlement report: ${rest.finance.settlementRows} rows${
                      rest.finance.settlementAmount != null
                        ? `, sample amount ${formatMoney(rest.finance.settlementAmount)}`
                        : ''
                    }.`
                  : ''}
              </li>
              <li>
                <Link className="underline" to={rest?.finance.href ?? '/amazon/finance'}>
                  Open finance
                </Link>
              </li>
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-traffic" title="Traffic Overview">
          {rest?.traffic.sessions != null || rest?.traffic.pageViews != null || rest?.traffic.domain ? (
            <ul className="space-y-1 text-sm">
              <li>
                Sessions (report sample): <strong>{rest?.traffic.sessions ?? '—'}</strong>
              </li>
              <li>
                Page views (report sample): <strong>{rest?.traffic.pageViews ?? '—'}</strong>
              </li>
              <li>
                Buy Box % (sample avg):{' '}
                <strong>
                  {rest?.traffic.buyBoxPct == null ? '—' : `${rest.traffic.buyBoxPct.toFixed(1)}%`}
                </strong>
              </li>
              <li className={shell.muted}>
                {rest?.traffic.records
                  ? `${rest.traffic.records} Sales & Traffic rows${rest.traffic.truncated ? ', page 1 only' : ''}. SKU grain, not a daily series.`
                  : 'No Sales & Traffic rows yet.'}
              </li>
            </ul>
          ) : (
            <p className={shell.muted}>
              No Sales &amp; Traffic report rows on this connection. Sessions stay off until that
              report syncs.
            </p>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-health" title="Business Health">
          {brief ? (
            <ul className="space-y-1 text-sm">
              <li>
                Score:{' '}
                <strong className="text-2xl tabular-nums">
                  {brief.health.overall ?? '—'}
                </strong>{' '}
                ({brief.health.band})
              </li>
              {brief.health.components.map((c) => (
                <li key={c.id}>
                  {c.label}: {c.score ?? '—'}
                </li>
              ))}
            </ul>
          ) : (
            <p className={shell.muted}>Waiting on Daily Brief.</p>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-returns" title="Returns">
          {rest && rest.returns.rows === 0 && rest.returns.sample === 0 ? (
            <p className={shell.muted}>No Amazon returns report rows yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              <li>
                Report rows: <strong>{rest?.returns.rows ?? 0}</strong>
              </li>
              <li>
                Units on page 1: <strong>{rest?.returns.units ?? '—'}</strong>
              </li>
              <li className={shell.muted}>
                {rest?.returns.truncated ? 'Page 1 only.' : 'Full stored report page.'} Overlay
                plots return units on days that have a return request date.
              </li>
              <li>
                FBA shipments report rows: <strong>{rest?.fbaShipments.rows ?? 0}</strong>
                {rest?.fbaShipments.truncated ? ' (page 1)' : ''}
              </li>
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-cod" title="COD vs Prepaid">
          {rest?.payment.sales == null ? (
            <p className={shell.muted}>Waiting on sales for the selected period.</p>
          ) : rest.payment.classifiedOrders === 0 ? (
            <p className={shell.muted}>
              {rest.payment.periodLabel} sales {formatMoney(rest.payment.sales, rest.payment.currency)}.
              Payment split needs order payment method for those Amazon order IDs.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              <li className={shell.muted}>{rest.payment.periodLabel}</li>
              <li>
                COD:{' '}
                <strong>{formatMoney(rest.payment.codSales, rest.payment.currency)}</strong>
                {rest.payment.codItems ? ` · ${rest.payment.codItems} items` : ''}
              </li>
              <li>
                Prepaid:{' '}
                <strong>{formatMoney(rest.payment.prepaidSales, rest.payment.currency)}</strong>
                {rest.payment.prepaidItems ? ` · ${rest.payment.prepaidItems} items` : ''}
              </li>
              {rest.payment.unclassifiedSales > 0 ? (
                <li>
                  Unmatched:{' '}
                  <strong>{formatMoney(rest.payment.unclassifiedSales, rest.payment.currency)}</strong>
                </li>
              ) : null}
              <li className={shell.muted}>
                {rest.payment.classifiedOrders} of {rest.payment.orders} orders have a payment method.
              </li>
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(6)}>
        <Card id="ops-swot" title="SWOT">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="mb-1 font-medium">Strengths</h3>
              {swot.strengths.length === 0 ? (
                <p className={shell.muted}>None from current ops rules.</p>
              ) : (
                <ul className="list-disc pl-4">
                  {swot.strengths.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="mb-1 font-medium">Risks</h3>
              {swot.risks.length === 0 ? (
                <p className={shell.muted}>None from current ops rules.</p>
              ) : (
                <ul className="list-disc pl-4">
                  {swot.risks.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div className={colSpanClass(12)}>
        <Card id="ops-activity" title="Activity Feed">
          {domains.length === 0 ? (
            <p className={shell.muted}>No sync timestamps yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {domains.map((d) => (
                <li key={d.key} className="flex justify-between gap-3">
                  <span>{activityTitle(d.key)}</span>
                  <span className={shell.muted}>{formatSyncTime(d.lastSync)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(12)}>
        <Card id="timeline" title="Timeline">
          {timeline.length === 0 ? (
            <p className={shell.muted}>No daily sales points yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {timeline.map((r) => (
                <li key={r.iso} className="grid grid-cols-4 gap-2 tabular-nums">
                  <span>{r.label}</span>
                  <span>{r.gmv == null ? '—' : Math.round(r.gmv).toLocaleString('en-IN')}</span>
                  <span>{r.items ?? '—'}</span>
                  <span>{r.units ?? '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className={colSpanClass(12)}>
        <Card id="executive-summary" title="Executive Summary">
          <p className="text-sm leading-relaxed">{summary}</p>
        </Card>
      </div>
    </div>
  );
}
